import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Avatar, Grow } from '@mui/material';
import { CloudUpload, History, Update, DeviceHub, SettingsInputHdmi } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../App';

function FirmwarePage() {
  const { token } = useAuth();
  const [firmware, setFirmware] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    device_type: '',
    version: '',
    description: '',
    file: null,
  });

  useEffect(() => {
    fetchFirmware();
  }, [token]);

  const fetchFirmware = async () => {
    try {
      const response = await axios.get(
        '/api/v1/firmware/',
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFirmware(response.data);
    } catch (err) {
      console.error('Failed to fetch firmware:', err);
    }
  };

  const handleUpload = async () => {
    try {
      const data = new FormData();
      data.append('device_type', formData.device_type);
      data.append('version', formData.version);
      data.append('description', formData.description);
      data.append('file', formData.file);

      await axios.post(
        '/api/v1/firmware/',
        data,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setOpen(false);
      fetchFirmware();
      setFormData({ device_type: '', version: '', description: '', file: null });
    } catch (err) {
      console.error('Failed to upload firmware:', err);
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: -1 }}>
            Firmware Repository
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            Manage and propagate software batches to edge nodes
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<CloudUpload />}
          onClick={() => setOpen(true)}
          sx={{
            borderRadius: 3,
            px: 3,
            py: 1.2,
            background: 'linear-gradient(45deg, #00B4DB, #0083B0)',
            boxShadow: '0 8px 16px rgba(0, 180, 219, 0.2)'
          }}
        >
          Propagate Patch
        </Button>
      </Box>

      <Grow in>
        <TableContainer component={Paper} className="glass-card" sx={{ borderRadius: 4, overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'rgba(0, 180, 219, 0.05)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Node Ecosystem</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Batch Version</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Strategic Description</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Deployment Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {firmware.map((fw, idx) => (
                <TableRow key={fw.id} sx={{ '&:hover': { bgcolor: 'rgba(0, 180, 219, 0.02)' }, transition: 'background 0.2s' }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'rgba(0, 180, 219, 0.1)', color: 'primary.main', width: 32, height: 32 }}>
                        <DeviceHub sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{fw.device_type}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`v${fw.version}`}
                      size="small"
                      sx={{ fontWeight: 900, borderRadius: 1, bgcolor: 'primary.light', color: 'white' }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>{fw.description || 'General maintenance patch.'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <History sx={{ fontSize: 16, opacity: 0.6 }} />
                      <Typography variant="caption" sx={{ fontWeight: 800 }}>{new Date(fw.created_at).toLocaleString()}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {firmware.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                    <Update sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.2, mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      No firmware batches detected in the current repository.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grow>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { borderRadius: 5, p: 2, backdropFilter: 'blur(22px)', minWidth: 500 } }}
      >
        <DialogTitle sx={{ fontWeight: 900, fontSize: '1.5rem' }}>Synchronize Firmware Patch</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontWeight: 600 }}>
            Specify the target ecosystem and patch parameters. Ensure binary integrity.
          </Typography>
          <TextField
            fullWidth
            label="Target Ecosystem / Model"
            value={formData.device_type}
            onChange={(e) => setFormData({ ...formData, device_type: e.target.value })}
            margin="normal"
            required
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />
          <TextField
            fullWidth
            label="Patch Revision Version"
            value={formData.version}
            onChange={(e) => setFormData({ ...formData, version: e.target.value })}
            margin="normal"
            required
            placeholder="e.g., 2.4.0-Stable"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />
          <TextField
            fullWidth
            label="Operational Changelog"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />
          <Box sx={{ mt: 3, p: 3, border: '2px dashed #00B4DB', borderRadius: 4, textAlign: 'center', bgcolor: 'rgba(0, 180, 219, 0.05)' }}>
            <CloudUpload sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: 'primary.main' }}>Select Binary Package</Typography>
            <input
              type="file"
              onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
              style={{ marginTop: 12, fontSize: '0.8rem' }}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpen(false)} sx={{ fontWeight: 700, color: 'text.secondary' }}>Abort</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            sx={{ borderRadius: 3, px: 4, fontWeight: 900 }}
          >
            Launch Propagation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FirmwarePage;
